/**
 * Reducer wiring for the in-house dva replacement (ENG-4570).
 *
 * Ports dva-core's prefixNamespace / prefixType / getReducer / handleActions so
 * model reducers keep their `namespace/reducerName` action contract and the
 * effect-internal `put({ type: 'bareName' })` auto-prefix semantics.
 */
import { AnyAction, Reducer } from 'redux'

export const NAMESPACE_SEP = '/'

export type Model = {
  namespace: string
  state?: any
  reducers?: Record<string, any> | [Record<string, any>, ...any[]]
  effects?: Record<string, any>
}

const prefix = (obj: Record<string, any>, namespace: string): Record<string, any> => {
  const result: Record<string, any> = {}
  for (const key of Object.keys(obj)) {
    result[`${namespace}${NAMESPACE_SEP}${key}`] = obj[key]
  }
  return result
}

/**
 * Prefix a model's reducer/effect keys with its namespace. Operates on a shallow
 * copy so the caller's model object is left untouched.
 */
export const prefixNamespace = (model: Model): Model => {
  const next: Model = { ...model }
  const { namespace, reducers, effects } = next

  if (reducers) {
    if (Array.isArray(reducers)) {
      const [reducer, ...rest] = reducers
      next.reducers = [prefix(reducer, namespace), ...rest] as [Record<string, any>, ...any[]]
    } else {
      next.reducers = prefix(reducers, namespace)
    }
  }
  if (effects) {
    next.effects = prefix(effects, namespace)
  }
  return next
}

/**
 * Resolve the action type a `put`/`take` inside an effect should dispatch.
 * Bare types local to the model are prefixed with the namespace; types that do
 * not resolve to a local reducer/effect (e.g. cross-namespace `events/foo`) pass
 * through unchanged. Mirrors dva-core's prefixType exactly.
 */
export const prefixType = (type: string, model: Model): string => {
  const prefixedType = `${model.namespace}${NAMESPACE_SEP}${type}`
  const typeWithoutAffix = prefixedType.replace(/\/@@[^/]+?$/, '')

  const reducers = Array.isArray(model.reducers) ? model.reducers[0] : model.reducers
  const reducer = reducers && reducers[typeWithoutAffix]
  if (reducer || (model.effects && model.effects[typeWithoutAffix])) {
    return prefixedType
  }
  return type
}

const identity = (value: any) => value

/**
 * Build a slice reducer from a map of `{ fullActionType: handler }`, applying
 * the matching handler for each dispatched action. Equivalent to dva-core's
 * handleActions.
 */
const handleActions = (
  handlers: Record<string, any>,
  defaultState: any,
): Reducer<any, AnyAction> => {
  return (state = defaultState, action) => {
    const handler = handlers[action.type]
    return (handler || identity)(state, action)
  }
}

/**
 * Produce a slice reducer for a model. Supports the array form
 * `[handlers, enhancer]` for parity with dva-core, though current models use the
 * plain-object form.
 */
export const getReducer = (
  reducers: Record<string, any> | [Record<string, any>, ...any[]] | undefined,
  state: any,
): Reducer<any, AnyAction> => {
  if (Array.isArray(reducers)) {
    const [handlers, enhancer] = reducers
    return enhancer(handleActions(handlers, state))
  }
  return handleActions(reducers || {}, state)
}
